import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * SectorScoresCard Component
 * 
 * Compact table display of sector performance scores
 */
const SectorScoresCard = ({ sectors }) => {
  const { t, i18n } = useTranslation();

  if (!sectors) return null;

  // Transform array format to object format if needed
  let sectorsObj = sectors;
  if (Array.isArray(sectors)) {
    sectorsObj = {};
    sectors.forEach((item) => {
      const sectorField = item.sector || item.sectorCode || '';
      let sectorName = '';
      let bilingualName = { en: '', vi: '' };
      
      if (typeof sectorField === 'string') {
        sectorName = sectorField;
        bilingualName = { en: sectorName, vi: sectorName };
      } else if (typeof sectorField === 'object' && sectorField !== null) {
        bilingualName = {
          en: sectorField.en || sectorField.name_en || '',
          vi: sectorField.vi || sectorField.name_vi || sectorField.en || ''
        };
        sectorName = bilingualName.en || bilingualName.vi || '';
      }
      
      sectorsObj[sectorName] = {
        score: item.score || 0,
        name: bilingualName
      };
    });
  }

  if (Object.keys(sectorsObj).length === 0) return null;

  const sectorEntries = Object.entries(sectorsObj).sort((a, b) => (b[1].score || 0) - (a[1].score || 0));

  return (
    <Box>
      {/* Compact Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
          {t('riskManagement.portfolioConstruction.overview.topPerformingSectors')}
        </Typography>
        <Chip 
          label={`${Object.keys(sectorsObj).length} ${t('riskManagement.portfolioConstruction.overview.sectorsCount')}`}
          size="small"
          sx={{ 
            bgcolor: 'rgba(102, 126, 234, 0.1)',
            color: '#667eea',
            fontWeight: 600,
            height: 22,
            fontSize: '0.7rem',
          }}
        />
      </Box>

      {/* Compact Table Layout */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: 1.5,
      }}>
        {sectorEntries.map(([sectorCode, sectorData]) => {
          const score = sectorData.score || 0;
          const sectorName = sectorData.name 
            ? (typeof sectorData.name === 'object' ? sectorData.name[i18n.language] || sectorData.name.en || sectorData.name.vi : sectorData.name)
            : sectorCode || 'Unknown';

          const getColor = (score) => {
            if (score >= 80) return '#4caf50';
            if (score >= 60) return '#ff9800';
            return '#f44336';
          };

          const getStatus = (score) => {
            if (score >= 80) return 'Strong';
            if (score >= 60) return 'Moderate';
            return 'Weak';
          };

          const color = getColor(score);

          return (
            <Box 
              key={sectorCode}
              sx={{ 
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'rgba(0,0,0,0.08)',
                bgcolor: 'white',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: color,
                  boxShadow: `0 2px 8px ${color}20`,
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    color: 'text.primary',
                    letterSpacing: '0.3px',
                  }}
                >
                  {sectorName}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 800,
                    color: color,
                    fontSize: '1.1rem',
                    lineHeight: 1,
                  }}
                >
                  {score.toFixed(0)}
                </Typography>
              </Box>
              
              <Chip 
                label={getStatus(score)}
                size="small"
                sx={{ 
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: `${color}15`,
                  color: color,
                  '& .MuiChip-label': { px: 0.75 },
                  mb: 0.75,
                }}
              />

              {/* Slim Progress Bar */}
              <Box sx={{ 
                height: 4,
                bgcolor: 'rgba(0,0,0,0.06)',
                borderRadius: 1,
                overflow: 'hidden',
              }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${score}%`,
                    bgcolor: color,
                    borderRadius: 1,
                    transition: 'width 0.6s ease',
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectorScoresCard;
